import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { API_URL } from "../config/api";
import PlaceInput from "../components/PlaceInput";
import { Ship, Loader2, AlertCircle, CheckCircle2, RefreshCw, Anchor } from "lucide-react";

const EQUIP_OPTIONS = [
  { value: "20GP", label: "20' General Purpose (20GP)" },
  { value: "40GP", label: "40' General Purpose (40GP)" },
  { value: "40HC", label: "40' High Cube (40HC)" },
  { value: "20RE", label: "20' Reefer (20RE)" },
  { value: "40RH", label: "40' Reefer High Cube (40RH)" },
];

const HAULAGE = [
  { value: "CY", label: "CY — port/ramp (merchant haulage)" },
  { value: "SD", label: "SD — store door (carrier haulage)" },
];

export default function HapagSpotPanel() {
  const token = localStorage.getItem("token");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    placeOfReceipt: "",
    placeOfDelivery: "",
    equipmentSizeType: "40HC",
    numberOfContainers: 1,
    weightPerContainerKg: 18000,
    receiptTypeAtOrigin: "CY",
    deliveryTypeAtDestination: "CY",
    earliestDepartureDate: "",
    commodity: "FAK",
  });
  const [porLabel, setPorLabel] = useState("");
  const [podLabel, setPodLabel] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadStatus = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/hapag/status`, { headers: { Authorization: `Bearer ${token}` } });
      setStatus(data);
    } catch {
      setStatus({ configured: false });
    }
  };
  useEffect(() => { loadStatus(); /* eslint-disable-next-line */ }, []);

  const search = async () => {
    if (!form.placeOfReceipt || !form.placeOfDelivery) {
      toast.error("Enter Place of Receipt and Place of Delivery (UN/LOCODE)");
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const { data } = await axios.post(
        `${API_URL}/hapag/spot/search`,
        {
          placeOfReceipt: form.placeOfReceipt.trim().toUpperCase(),
          placeOfDelivery: form.placeOfDelivery.trim().toUpperCase(),
          equipmentSizeType: form.equipmentSizeType,
          numberOfContainers: Number(form.numberOfContainers) || 1,
          weightPerContainerKg: Number(form.weightPerContainerKg) || 18000,
          receiptTypeAtOrigin: form.receiptTypeAtOrigin,
          deliveryTypeAtDestination: form.deliveryTypeAtDestination,
          earliestDepartureDate: form.earliestDepartureDate || null,
          commodity: form.commodity || "FAK",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(data);
      if (!data.available_count) {
        toast.warning("Hapag returned no bookable spot offers for this lane/date");
      } else {
        toast.success(`Hapag: ${data.available_count} spot offer(s) · best ${data.best_offer.total_amount} ${data.best_offer.currency}`);
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : (detail?.message || e.message || "Request failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="hapag-spot-panel">
      {/* Status banner */}
      <div className={`rounded-xl border p-4 ${status?.configured ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Ship className={`h-5 w-5 ${status?.configured ? "text-emerald-600" : "text-amber-600"}`} />
            <div>
              <div className="font-semibold text-slate-800">Hapag-Lloyd Quick Quotes Spot</div>
              <div className="text-xs text-slate-500">
                {status?.configured
                  ? <>Connected · {status.environment}{status.customer_identifier_set ? "" : " · set HAPAG_CUSTOMER_IDENTIFIER"}</>
                  : "Not configured — add HAPAG_CLIENT_ID / HAPAG_CLIENT_SECRET"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {status?.configured
              ? <Badge className="bg-emerald-600 text-xs" data-testid="hapag-status-ok">Prices API</Badge>
              : <Badge variant="outline" className="text-xs">Setup</Badge>}
            <Button size="sm" variant="outline" onClick={loadStatus} data-testid="hapag-retest-btn"><RefreshCw className="h-4 w-4 mr-1" /> Re-test</Button>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="rounded-xl border p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Place of Receipt (port, city or door)</Label>
            <PlaceInput kind="all" allowFreeText={false} value={porLabel}
              placeholder="Search — e.g. Koper, Bratislava…" data-testid="hapag-por"
              onSelect={(p) => { set("placeOfReceipt", p.code); setPorLabel(p.label || `${p.name} (${p.code})`); }} />
          </div>
          <div>
            <Label className="text-xs">Place of Delivery (port, city or door)</Label>
            <PlaceInput kind="all" allowFreeText={false} value={podLabel}
              placeholder="Search — e.g. Shanghai…" data-testid="hapag-pod"
              onSelect={(p) => { set("placeOfDelivery", p.code); setPodLabel(p.label || `${p.name} (${p.code})`); }} />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs">Equipment</Label>
            <select data-testid="hapag-equipment" className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={form.equipmentSizeType} onChange={(e) => set("equipmentSizeType", e.target.value)}>
              {EQUIP_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Qty</Label>
            <Input data-testid="hapag-qty" type="number" min="1" value={form.numberOfContainers} onChange={(e) => set("numberOfContainers", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Weight/ctr (kg)</Label>
            <Input data-testid="hapag-weight" type="number" value={form.weightPerContainerKg} onChange={(e) => set("weightPerContainerKg", e.target.value)} />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs">Departure date (≥4 days out)</Label>
            <Input data-testid="hapag-date" type="date" value={form.earliestDepartureDate} onChange={(e) => set("earliestDepartureDate", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Origin haulage</Label>
            <select data-testid="hapag-receipt-type" className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={form.receiptTypeAtOrigin} onChange={(e) => set("receiptTypeAtOrigin", e.target.value)}>
              {HAULAGE.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Delivery haulage</Label>
            <select data-testid="hapag-delivery-type" className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={form.deliveryTypeAtDestination} onChange={(e) => set("deliveryTypeAtDestination", e.target.value)}>
              {HAULAGE.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
        <Button onClick={search} disabled={loading || !status?.configured} data-testid="hapag-search-btn" className="bg-[#0b3d6b] hover:bg-[#0a3358]">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Anchor className="h-4 w-4 mr-2" />}
          Get Hapag Spot rates
        </Button>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 flex items-start gap-2 text-sm text-red-700" data-testid="hapag-error">
          <AlertCircle className="h-4 w-4 mt-0.5" /> <span>{error}</span>
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="space-y-3" data-testid="hapag-results">
          {result.available_count === 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="hapag-no-offers">
              Hapag could not offer a bookable spot rate for this exact request ({result.offer_count} routing(s) found). Try an alternative routing, port/door, equipment or date.
            </div>
          )}
          {(result.offers || []).map((o, i) => (
            <div key={o.offer_ref || i} className={`rounded-xl border p-4 ${o.available ? "border-slate-200" : "border-slate-100 bg-slate-50/60"}`} data-testid={`hapag-offer-${i}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={o.product === "QUICK_QUOTES_SPOT" ? "bg-[#0b3d6b]" : "bg-slate-500"}>{o.product === "QUICK_QUOTES_SPOT" ? "Spot" : "Quick Quote"}</Badge>
                  <span className="font-medium text-slate-800">{o.pol || o.place_of_receipt} → {o.pod || o.place_of_delivery}</span>
                  {o.transit_days != null && <span className="text-xs text-slate-500">· {o.transit_days} days</span>}
                </div>
                <div className="text-right">
                  {o.available
                    ? <div className="text-lg font-bold text-slate-800" data-testid={`hapag-offer-price-${i}`}>{o.total_amount} {o.currency}</div>
                    : <span className="text-xs uppercase font-semibold text-amber-600">{o.error_reason || "Unavailable"}</span>}
                </div>
              </div>
              {o.valid_until && <div className="text-xs text-slate-400 mt-1">Valid until {o.valid_until}</div>}
              {o.available && o.charges?.length > 0 && (
                <div className="mt-2 border-t pt-2 text-xs text-slate-600 space-y-0.5">
                  {o.charges.filter((c) => c.amount).slice(0, 8).map((c, ci) => (
                    <div key={ci} className="flex justify-between">
                      <span>{c.label || c.code}</span>
                      <span className="font-medium">{c.amount} {c.currency}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
